/**
 * Fetch grammar data from Supabase. Used by GrammarDetailScreen and GrammarScreen.
 * Falls back to null if tables don't exist or fetch fails.
 */
import { supabase } from '../supabase';

export interface GrammarTopicRow {
  type: string;
  title_it: string;
  title_bn: string;
  title_en: string;
  icon?: string;
  color?: string;
  sort_order: number;
}

export async function fetchGrammarTopics(): Promise<GrammarTopicRow[] | null> {
  try {
    const { data, error } = await supabase
      .from('grammar_topics')
      .select('type, title_it, title_bn, title_en, icon, color, sort_order')
      .order('sort_order');
    if (error) return null;
    return data || [];
  } catch {
    return null;
  }
}

export async function fetchGrammarTopicByType(topicType: string): Promise<{ title_it: string; title_bn: string; title_en: string } | null> {
  try {
    const { data, error } = await supabase
      .from('grammar_topics')
      .select('title_it, title_bn, title_en')
      .eq('type', topicType)
      .maybeSingle();
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
}

export interface GrammarRuleRow {
  italian: string;
  bengali: string;
}

export interface GrammarExampleRow {
  id: string;
  italian: string;
  bengali: string;
  english?: string;
}

export interface VerbConjugationRow {
  person: string;
  personBn: string;
  form: string;
  formBn: string;
}

export interface VerbDataRow {
  id: string;
  infinitive: string;
  infinitiveBn: string;
  english?: string;
  conjugations: VerbConjugationRow[];
}

export interface GrammarDataFromApi {
  rules: GrammarRuleRow[];
  examples: GrammarExampleRow[] | VerbDataRow[];
}

export async function fetchGrammarByTopic(topicType: string): Promise<GrammarDataFromApi | null> {
  try {
    const [rulesRes, examplesRes, verbsRes] = await Promise.all([
      supabase.from('grammar_rules').select('italian, bengali').eq('topic_type', topicType).order('sort_order'),
      supabase.from('grammar_examples').select('id, italian, bengali, english').eq('topic_type', topicType).order('sort_order'),
      supabase.from('grammar_verbs').select('id, infinitive, infinitive_bn, english').eq('topic_type', topicType).order('sort_order'),
    ]);

    const rules: GrammarRuleRow[] = (rulesRes.data || []).map((r) => ({ italian: r.italian, bengali: r.bengali }));

    // If verbs topic and we have verbs, return verbs as examples (with conjugations)
    if (topicType === 'verbs' && verbsRes.data && verbsRes.data.length > 0) {
      const verbIds = verbsRes.data.map((v) => v.id);
      const { data: conjData } = await supabase
        .from('grammar_verb_conjugations')
        .select('verb_id, person, person_bn, form, form_bn, sort_order')
        .in('verb_id', verbIds)
        .order('sort_order');
      const conjByVerb: Record<string, { person: string; personBn: string; form: string; formBn: string }[]> = {};
      (conjData || []).forEach((c) => {
        if (!conjByVerb[c.verb_id]) conjByVerb[c.verb_id] = [];
        conjByVerb[c.verb_id].push({
          person: c.person,
          personBn: c.person_bn,
          form: c.form,
          formBn: c.form_bn,
        });
      });
      const verbs: VerbDataRow[] = verbsRes.data.map((v) => ({
        id: v.id,
        infinitive: v.infinitive,
        infinitiveBn: v.infinitive_bn,
        english: v.english || undefined,
        conjugations: conjByVerb[v.id] || [],
      }));
      return { rules, examples: verbs };
    }

    // Simple examples
    const examples: GrammarExampleRow[] = (examplesRes.data || []).map((e) => ({
      id: e.id,
      italian: e.italian,
      bengali: e.bengali,
      english: e.english || undefined,
    }));
    return { rules, examples };
  } catch {
    return null;
  }
}
